import { CardWithRelations } from "@/hooks/useCards";
import { BinderVariant } from "@/types/binders";
import { CardVariantWithBinder } from "@/types/varinats";
import { useCallback, useMemo, useState } from "react";
import { CardDetailsModalCollectionItem } from "./CardDetailsModalCollectionItem";
import { EditCardInBinderDialog } from "@/components/binder/add/EditCardInBinderDialog/EditCardInBinderDialog";
import { Button } from "@/components/ui/button";
import { ArrowDown, Plus } from "lucide-react";
import { SelectBinderVariantsModal } from "@/components/binder/edit/SelectBinderVariantsModal";
import { AddCardToBinderDialog } from "@/components/binder/add/AddCardToBinderDialog/AddCardToBinderDialog";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  card: CardWithRelations;
  variants: CardVariantWithBinder[] | null;
}

export function CardDetailsModalCollection({ card, variants }: Props) {
  const [selectedBinderVariant, setSelectedBinderVariant] =
    useState<BinderVariant | null>(null);

  const [isOpenEditCardInBinderDialog, setIsOpenEditCardInBinderDialog] =
    useState(false);

  const [isOpenShowMore, setIsOpenShowMore] = useState(false);

  const [isOpenAddToBinderDialog, setIsOpenAddToBinderDialog] = useState(false);

  const onAddToBinder = useCallback(() => {
    setIsOpenAddToBinderDialog(true);
  }, []);

  const onSelectBinderVariant = useCallback((binderVariant: BinderVariant) => {
    setSelectedBinderVariant(binderVariant);
    setIsOpenEditCardInBinderDialog(true);
  }, []);

  const onSelectShowMore = useCallback(() => {
    setIsOpenShowMore(true);
  }, []);

  const normalizedVariants = useMemo(() => {
    if (!variants) {
      return [];
    }

    const binderVariants: Array<BinderVariant> = [];

    variants.forEach((variant) => {
      variant.binders?.forEach((b2b) => {
        const binderVariant = {
          ...b2b.vtb,
          grade: b2b.grade,
          graded_card: b2b.graded_card,
          grading_company: b2b.grading_company,
          variant: {
            ...variant,
            card: card,
            condition: variant.condition,
          },
        } satisfies BinderVariant;

        binderVariants.push(binderVariant);
      });
    });

    return [...binderVariants].sort((a, b) => {
      const createdAtA = new Date(a.created_at);
      const createdAtB = new Date(b.created_at);
      return createdAtB.getTime() - createdAtA.getTime();
    });
  }, [card, variants]);

  const slicedVariants = useMemo(() => {
    return normalizedVariants.slice(0, 5);
  }, [normalizedVariants]);

  const isShowMore = normalizedVariants.length > 5;

  const moreCount = normalizedVariants.length - 5;

  const isEmpty = normalizedVariants.length === 0;

  if (!variants) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <>
        <Button onClick={onAddToBinder}>
          <Plus className="w-4 h-4" />
          Add to binder
        </Button>
        <AddCardToBinderDialog
          cardId={card.id}
          open={isOpenAddToBinderDialog}
          onOpenChange={setIsOpenAddToBinderDialog}
        />
      </>
    );
  }

  return (
    <div className="grid gap-4 border border-input rounded-lg">
      <div className="flex items-center justify-between p-6 pb-1">
        <h3 className="text-lg font-semibold">My collection</h3>
        <Button onClick={onAddToBinder} size="sm">
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </div>
      <div className="grid">
        {slicedVariants.map((binderVariant) => (
          <CardDetailsModalCollectionItem
            key={binderVariant.id}
            binderItem={binderVariant}
            onSelect={onSelectBinderVariant}
          />
        ))}
        {selectedBinderVariant && (
          <EditCardInBinderDialog
            key={selectedBinderVariant.id}
            open={isOpenEditCardInBinderDialog}
            onOpenChange={setIsOpenEditCardInBinderDialog}
            binderItem={selectedBinderVariant}
          />
        )}
        {isShowMore && (
          <Button variant="outline" onClick={onSelectShowMore}>
            <ArrowDown className="w-4 h-4" />
            Show All ({moreCount})
          </Button>
        )}
        <SelectBinderVariantsModal
          open={isOpenShowMore}
          onOpenChange={setIsOpenShowMore}
          binderItems={normalizedVariants}
          onSelectBinderItem={onSelectBinderVariant}
        />
        <AddCardToBinderDialog
          cardId={card.id}
          open={isOpenAddToBinderDialog}
          onOpenChange={setIsOpenAddToBinderDialog}
        />
      </div>
    </div>
  );
}
